import { type InputHTMLAttributes, type ReactNode, useId } from 'react';
import tw, { type TwStyle } from 'twin.macro';
import Label from '@components/elements/Label/Label';

type CheckboxProps = {
  cssExtend?: {
    wrapper?: TwStyle;
    innerWrapper?: TwStyle;
    input?: TwStyle;
    label?: TwStyle;
  };
  label?: ReactNode;
  errorMessage?: string;
  isRequired?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

const Checkbox = ({
  cssExtend,
  label,
  errorMessage,
  isRequired = false,
  disabled = false,
  ...inputProps
}: CheckboxProps) => {
  const id = useId();

  return (
    <div
      css={cssExtend?.wrapper}
      data-testid="Checkbox__wrapper"
      data-cy="Checkbox_div"
    >
      <div
        data-testid="Checkbox__innerWrapper"
        css={[
          tw`flex gap-x-3`,
          disabled && tw`opacity-70`,
          cssExtend?.innerWrapper,
        ]}
      >
        <input
          data-testid="Checkbox__input"
          css={[
            tw`mt-px w-5 h-5 shrink-0 cursor-pointer rounded-sm text-primary disabled:(opacity-70 cursor-not-allowed) focus-visible:(ring-2 ring-primary)`,
            errorMessage && tw`border-red-2`,
            cssExtend?.input,
          ]}
          id={id}
          disabled={disabled}
          {...inputProps}
          type="checkbox"
        />
        {label && (
          <Label
            data-testid="Checkbox__label"
            cssExtend={[
              disabled ? tw`cursor-not-allowed` : tw`cursor-pointer`,
              cssExtend?.label,
            ]}
            htmlFor={id}
            isError={!!errorMessage}
            isRequired={isRequired}
          >
            {label}
          </Label>
        )}
      </div>
      {errorMessage && (
        <p
          tw="mt-1 mb-0 text-sm text-red-2"
          data-testid="Checkbox__errorMessage"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default Checkbox;
